// Tension vocabulary (#236, upper structures): which tensions each catalogue
// chord type ADMITS, as semitone offsets from the root. Instrument-free on
// purpose — the piano's upper-structure enumerator consumes it today, and a
// guitar-comping tension layer can consume it unchanged later.
//
// The vocabulary is the standard seven: ♭9/9/♯9 = 1/2/3, 11/♯11 = 5/6,
// ♭13/13 = 8/9. Two principles decide the table, in order:
//
//   1. The chord's OWN intervals rule where they already declare a tension.
//      A dom7♭9's ♭9 is a chord tone, so a ♮9 beside it contradicts the
//      symbol and is removed; dom9's declared ♮9 removes the altered pair
//      the same way. And a tension that duplicates a chord tone's pitch
//      class is not a tension at all — that is the final filter, applied
//      mechanically, which is what makes maj9 admit no 9 and min6 no 13
//      without either being special-cased.
//
//   2. Standard practice fills the rest, CONSERVATIVELY where sources
//      differ (the reasons are inline). A tension left out here is a sound
//      somebody plays somewhere; a tension admitted here is offered by an
//      enumerator as a considered choice, so the bar is "uncontroversial",
//      not "attested".

import { CHORD_TYPES, type ChordTypeId } from './chords';

/** Display names for the tension offsets this module deals in. Offset 11 is
 * spoken for by exactly one chord: dim7's classic added tone a whole step
 * above the ♭♭7 — the "maj7" tension — named '7' as chords.ts names it. */
export const TENSION_NAMES: Record<number, string> = {
  1: '♭9',
  2: '9',
  3: '♯9',
  5: '11',
  6: '♯11',
  8: '♭13',
  9: '13',
  // The two sevenths are tensions only over the triad tier, which has
  // declared neither; 11 is also dim7's classic added tone.
  10: '♭7',
  11: '7',
};

const mod12 = (n: number) => ((n % 12) + 12) % 12;

type TriadId = 'maj' | 'min' | 'aug' | 'dim';

/** The seventh chord each triad is the body of — where its colours come from. */
const TRIAD_PARENT: Record<TriadId, ChordTypeId> = {
  maj: 'maj7',
  min: 'min7',
  aug: 'maj7s5',
  dim: 'dim7',
};

/** The sevenths a triad has not declared, and so admits. */
const TRIAD_SEVENTHS: Record<TriadId, number[]> = {
  maj: [10, 11],
  min: [10, 11],
  aug: [10, 11],
  dim: [9, 10, 11],
};

/** Pitch offsets of the W-H (whole-half) diminished scale — the diminished
 * chords' own diatonic world, used to keep the "whole step above each chord
 * tone" rule honest for °maj7 (see below). */
const WH_DIMINISHED = new Set([0, 2, 3, 5, 6, 8, 9, 11]);

/**
 * The type whose colour rule governs `typeId`: itself, except for the triad
 * tier, whose colours come from the seventh chord it is the body of. An
 * augmented TRIAD is a Lydian-augmented sound like the maj7♯5 it bodies, not
 * a minor one — which is the sort of thing a caller reasoning about a chord's
 * family needs to ask rather than infer from the id.
 */
export function tensionParent(typeId: ChordTypeId): ChordTypeId {
  return CHORD_TYPES[typeId].category === 'triad' ? TRIAD_PARENT[typeId as TriadId] : typeId;
}

/**
 * The tensions a chord type admits, as ascending semitone offsets from the
 * root. Deterministic and total over the catalogue; never empty in practice,
 * but callers must not assume that.
 */
export function admittedTensions(typeId: ChordTypeId): number[] {
  const type = CHORD_TYPES[typeId];
  // The root is a chord tone even when a rootless stack drops it from the
  // four voiced intervals — the chord is still named from it.
  const tones = new Set([0, ...type.intervals.map(mod12)]);

  let slots: number[];
  if (type.category === 'triad') {
    // A triad has DECLARED NO SEVENTH, so both are available to it: a ♭7
    // makes it a dominant, a ♮7 a major seventh, and over a diminished triad
    // the ♭♭7 as well — which is the °7 itself. Beyond that it colours like
    // the seventh chord it is the body of, so the rest of the set comes from
    // that parent: a bare major triad takes 9/♯11/13 the way a maj7 does, and
    // still never the ♮11 over its major 3rd.
    slots = [
      ...TRIAD_SEVENTHS[typeId as TriadId],
      ...admittedTensions(TRIAD_PARENT[typeId as TriadId]),
    ];
  } else if (type.category === 'diminished') {
    // The classic dim7 rule: a whole step above EACH chord tone (the "other"
    // diminished 7th — 9, 11, ♭13 and the major 7th over a plain °7). Applied
    // literally to °maj7 the step above its 7 lands on ♭9, which is not even
    // in the W-H diminished scale, so results are kept only when diatonic to
    // it — °maj7 gets 9/11/♭13. The ♭♭7-as-13 over °maj7 is deliberately NOT
    // admitted: the chord traded that tone away by name, and sounding both
    // makes a five-tone °7 cluster rather than a tension.
    slots = type.intervals.map((iv) => mod12(iv + 2)).filter((pc) => WH_DIMINISHED.has(pc));
  } else if (typeId === 'dom7sus4') {
    // Sus admits 9 and 13 only: the 4 IS the third-slot tone (not an 11
    // tension), and the altered ninths / ♭13 belong to sounds (sus♭9
    // phrygian) that iReal charts write as their own symbols.
    slots = [2, 9];
  } else if (type.category === 'dominant') {
    // The plain dominant admits everything except the natural 11 — the one
    // tension every source agrees stays off a major 3rd.
    slots = [1, 2, 3, 6, 8, 9];
    // ♮9 declared (dom9): drop the altered pair it contradicts, and ♭13 —
    // mixolydian ♭13 (melodic minor V) does carry ♮9+♭13, but a chart that
    // wants it writes 9♭13; conservative.
    if (tones.has(2)) slots = slots.filter((t) => ![1, 3, 8].includes(t));
    // ♮13 declared (dom13): ♭13 contradicts it; the altered ninths make the
    // H-W diminished 13♭9 sound, which again is its own symbol; conservative.
    if (tones.has(9)) slots = slots.filter((t) => ![1, 3, 8].includes(t));
    // ♭9 declared (dom7♭9): ♮9 contradicts it. The chord's home scale (H-W
    // diminished) carries ♮13 and ♯9 alongside, so those stay; it has no
    // ♭13, so that goes — conservative against the phrygian-dominant reading.
    if (tones.has(1)) slots = slots.filter((t) => ![2, 8].includes(t));
    // ♯9 declared (dom7♯9): the altered-scale reading — no ♮9, no ♮13. The
    // altered scale does carry ♭9 beside ♯9, but stacking both in one
    // voicing is contested; conservative, so ♭9 goes too.
    if (tones.has(3)) slots = slots.filter((t) => ![1, 2, 9].includes(t));
    // ♯5 declared (dom7♯5): no scale sounds a ♮13 a semitone over a ♯5
    // (whole-tone and altered both lack it); the ♭13 pc is the ♯5 itself
    // and falls to the duplicate filter.
    if (tones.has(8)) slots = slots.filter((t) => t !== 9);
  } else if (type.category === 'major') {
    // Ionian/Lydian: 9, ♯11, 13. Never ♭9/♯9/♭13 and never the natural 11
    // over the major 3rd. Lydian augmented (melodic minor III) carries all
    // three over maj7♯5, so no ♯5 adjustment is needed here; maj7♭5's ♯11
    // and the 6/9 types' 9/13 fall to the duplicate filter.
    slots = [2, 6, 9];
  } else if (tones.has(3) && tones.has(6)) {
    // Half-diminished (m7♭5 and its ♭9/♮9 extensions): 11 and ♭13, plus the
    // Locrian ♮2 9. A declared ♭9 (m7♭5♭9) contradicts the ♮9 and removes it.
    slots = [2, 5, 8];
    if (tones.has(1)) slots = slots.filter((t) => t !== 2);
  } else {
    // Minor 7ths and friends (dorian / melodic minor): 9, 11, 13. m7♯5's ♯5
    // is an enharmonic ♭13, so a ♮13 a semitone above it is out.
    slots = [2, 5, 9];
    if (tones.has(8)) slots = slots.filter((t) => t !== 9);
  }

  // A tension that duplicates a chord tone's pitch class is not a tension —
  // this is what strips maj9's 9, min6's 13, dom7♭5's ♯11, and so on, without
  // per-type lists.
  return [...new Set(slots.map(mod12))].filter((t) => !tones.has(t)).sort((a, b) => a - b);
}
